<script setup>

import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import TextInput from "@/Components/TextInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import { computed, ref, watch } from "vue";
import { useHelpers, useTenantTextRules } from "@/helpers";
import SearchSelect from "@/Components/SearchSelect.vue";
import MultiSearchSelect from "@/Components/MultiSearchSelect.vue";
import SelectInput from "@/Components/SelectInput.vue";

const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const currentUser = usePage().props.value.auth.user;
const projectProperty = computed(() => usePage().props.value.project);
const project = projectProperty.value;
watch(projectProperty, (value) => resetForm(value));

const props = defineProps({
  projectManagers: Array,
  companies: Array,
  colorField: Object,
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  }
});

const LEGACY_COLOR_VALUE = '__legacy_color__';
const colorFieldMode = computed(() => props.colorField?.mode || 'color_ids');
const isStringOptionsColorField = computed(() => colorFieldMode.value === 'string_options');
const normalizeStringColorValue = (value) => (
  value == null || value === '' ? null : String(value)
);
const colorOptions = computed(() =>
  (props.colorField?.options || [])
    .map((option) => {
      if (isStringOptionsColorField.value) {
        const normalizedValue = normalizeStringColorValue(option?.value);
        return {
          label: String(option?.label ?? ''),
          value: normalizedValue,
        };
      }

      const rawValue = option?.value ?? null;
      const normalizedValue = rawValue == null ? null : Number(rawValue);
      return {
        label: String(option?.label ?? ''),
        value: Number.isNaN(normalizedValue) ? null : normalizedValue,
      };
    })
    .filter(option => option.label && option.value != null)
);
const colorOptionIds = computed(() => new Set(colorOptions.value.map(c => String(c.value))));
const getLegacyColorLabel = (projectData) => {
  if (isStringOptionsColorField.value) {
    return null;
  }

  if (projectData?.color == null || projectData?.color === '') {
    return null;
  }

  const normalized = String(projectData.color).trim();
  if (!normalized || colorOptionIds.value.has(normalized)) {
    return null;
  }

  return normalized;
};
const normalizeProjectColors = (projectData) => {
  if (isStringOptionsColorField.value) {
    return [];
  }

  const colors = [];
  const legacyColorLabel = getLegacyColorLabel(projectData);

  if (legacyColorLabel) {
    colors.push(LEGACY_COLOR_VALUE);
  }

  const primaryColor = projectData?.color == null ? null : String(projectData.color).trim();
  if (!legacyColorLabel && primaryColor && colorOptionIds.value.has(primaryColor)) {
    colors.push(Number(primaryColor));
  }

  (projectData?.colors || []).forEach((color) => {
    const colorId = color?.colorID ?? color?.id ?? color?.pivot?.colorID ?? null;
    if (colorId == null) {
      return;
    }

    const normalized = Number(colorId);
    if (!Number.isNaN(normalized) && !colors.includes(normalized)) {
      colors.push(normalized);
    }
  });

  return colors;
};

const form = useForm({
  _method: 'POST',
  project: {
    client: project.client != null && project.client !== '' ? Number(project.client) : null,
    project_manager: (!project.pid && helpers.userIsProjectManager(currentUser)) ? currentUser.employeeID : (project.project_manager || null),
    project_manager1: project.project_manager1 || null,
    project_manager2: project.project_manager2 || null,
    projectname: project.projectname,
    color: props.colorField?.mode === 'string_options'
      ? normalizeStringColorValue(project.color)
      : (project.color || null),
    colors: normalizeProjectColors(project),
    materialtype: project.materialtype,
    stock_or_custom: project.stock_or_custom || 0,
  }
});

watch(() => form.project.colors, (value) => {
  if (isStringOptionsColorField.value) {
    if (Array.isArray(value) && value.length > 0) {
      form.project.colors = [];
    }
    return;
  }

  if (!Array.isArray(value)) {
    form.project.colors = [];
    return;
  }

  const legacyColorLabel = getLegacyColorLabel({ color: form.project.color });
  if (legacyColorLabel && !value.includes(LEGACY_COLOR_VALUE)) {
    form.project.color = null;
  }
}, { deep: true });

const saving = ref(false);
const saveForm = () => {
  saving.value = true;
  let endpoint = route('admin.projects.store');
  if (project.pid) {
    form._method = 'PUT';
    endpoint = route('admin.projects.update', { project: project.pid });
  }

  const projectPayload = {
    ...form.project,
    color: isStringOptionsColorField.value ? (form.project.color || null) : form.project.color,
  };

  if (!isStringOptionsColorField.value) {
    projectPayload.colors = form.project.colors.filter((colorId) => colorId !== LEGACY_COLOR_VALUE);
  }

  const payload = {
    ...form.data(),
    project: projectPayload
  };

  form.transform(() => payload).post(endpoint, {
    preserveScroll: true,
    onFinish() {
      saving.value = false;
      form.transform((data) => data);
    }
  });
  saving.value = false;
}

const resetForm = (projectData) => {
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }
  project.pid = projectData.pid;
  Object.keys(form.data().project).forEach(key => {
    if (key === 'colors') {
      form.project[key] = normalizeProjectColors(projectData);
      return;
    }

    if (key === 'client') {
      const rawClient = projectData[key];
      form.project.client = rawClient != null && rawClient !== '' ? Number(rawClient) : null;
      return;
    }

    if (key === 'color' && isStringOptionsColorField.value) {
      form.project.color = normalizeStringColorValue(projectData[key]);
      return;
    }

    form.project[key] = projectData[key];
  });
}

const companyOptions = computed(() =>
  (props.companies || [])
    .filter((company) => company?.client && (company?.ID != null || company?.id != null))
    .map((company) => ({
      label: company.client,
      value: Number(company.ID ?? company.id),
    }))
);

const companyModel = computed({
  get: () => (
    form.project.client != null && form.project.client !== ''
      ? Number(form.project.client)
      : null
  ),
  set: (v) => { form.project.client = v == null ? null : Number(v); },
});

const colorModel = computed({
  get: () => (
    form.project.color != null && form.project.color !== ''
      ? String(form.project.color)
      : null
  ),
  set: (value) => {
    form.project.color = value == null ? null : String(value);
  },
});

const selectedColorOptions = computed(() => {
  if (isStringOptionsColorField.value) {
    return [];
  }

  const optionsByValue = new Map(colorOptions.value.map((option) => [String(option.value), option]));

  return (form.project.colors || []).map((colorId) => {
    if (colorId === LEGACY_COLOR_VALUE) {
      return {
        label: getLegacyColorLabel({ color: form.project.color }) || '',
        value: LEGACY_COLOR_VALUE,
      };
    }

    return optionsByValue.get(String(colorId));
  }).filter(Boolean);
});
const colorLabel = computed(() => resolveTextRule(
  project.pid ? 'project.basic.color_label.edit' : 'project.basic.color_label.create',
  'Colors',
  project.pid ? 'project.basic.color_label.edit' : 'project.basic.color_label.create'
));
const materialTypeLabel = computed(() =>
  resolveTextRule('project.basic.material_type_label', 'Type of Material', 'project.basic.material_type_label')
);
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md">
    <div class="sm:grid grid-cols-6 gap-6">
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="company" value="Choose Company" :required="true" />
        <SearchSelect v-model="companyModel" :options="companyOptions" placeholder="Please select..."
          :disabled="!helpers.userCanManageProject(currentUser)" />
        <InputError class="mt-2" :message="form.errors['project.client']" />
      </div>
      <!-- Primary Project Manager -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="primary-project-manager" value="Primary Project Manager" :required="true" />
        <SelectInput id="primary-project-manager" v-model="form.project.project_manager"
          :disabled="(!helpers.userCanManageProject(currentUser) || (!project.pid && helpers.userIsProjectManager(currentUser)))"
          class="mt-1 disabled:bg-gray-50">
          <option :value="null">Please select...</option>
          <option v-for="manager in projectManagers" :value="manager.employeeID" v-text="manager.full_name"></option>
        </SelectInput>
        <InputError class="mt-2" :message="form.errors['project.project_manager']" />
      </div>
      <!-- Secondary Project Manager 1 -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="secondary-project-manager-1" value="Secondary Project Manager 1" />
        <SelectInput id="secondary-project-manager-1" v-model="form.project.project_manager1"
          :disabled="(!helpers.userCanManageProject(currentUser))"
          class="mt-1 disabled:bg-gray-50">
          <option :value="null">Please select...</option>
          <option v-for="manager in projectManagers" :value="manager.employeeID" v-text="manager.full_name"></option>
        </SelectInput>
        <InputError class="mt-2" :message="form.errors['project.project_manager1']" />
      </div>
      <!-- Secondary Project Manager 2 -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="secondary-project-manager-2" value="Secondary Project Manager 2" />
        <SelectInput id="secondary-project-manager-2" v-model="form.project.project_manager2"
          :disabled="(!helpers.userCanManageProject(currentUser))"
          class="mt-1 disabled:bg-gray-50">
          <option :value="null">Please select...</option>
          <option v-for="manager in projectManagers" :value="manager.employeeID" v-text="manager.full_name"></option>
        </SelectInput>
        <InputError class="mt-2" :message="form.errors['project.project_manager2']" />
      </div>
      <!-- Project Name -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="project-name" value="Project Name" :required="true" />
        <TextInput id="project-name" type="text" class="mt-1 block w-full" autocomplete="project-name"
          v-model="form.project.projectname"
          :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
        <InputError class="mt-2" :message="form.errors['project.projectname']" />
      </div>
      <!-- Color -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="color" :value="colorLabel" />
        <MultiSearchSelect
          v-if="!isStringOptionsColorField"
          v-model="form.project.colors"
          :options="colorOptions"
          :selected-options="selectedColorOptions"
          placeholder="Please select..."
          :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
        />
        <SearchSelect
          v-else
          v-model="colorModel"
          :options="colorOptions"
          placeholder="Please select..."
          :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
        />
        <InputError class="mt-2" :message="form.errors['project.colors']" />
        <InputError class="mt-2" :message="form.errors['project.colors.0']" />
        <InputError class="mt-2" :message="form.errors['project.color']" />
      </div>
      <!-- Type of Material -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="material-type" :value="materialTypeLabel" />
        <TextInput id="material-type" type="text" class="mt-1 block w-full" autocomplete="material-type"
          v-model="form.project.materialtype"
          :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))" />
        <InputError class="mt-2" :message="form.errors['project.materialtype']" />
      </div>
      <!-- Notification To -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="notification-to" value="Notification To" />
        <SelectInput id="notification-to" v-model="form.project.stock_or_custom"
          :disabled="(!helpers.userCanManageProject(currentUser))"
          class="mt-1 disabled:bg-gray-50">
          <option :value="0">Please select...</option>
          <option :value="1">Stock</option>
          <option :value="2">Custom</option>
        </SelectInput>
        <InputError class="mt-2" :message="form.errors['project.stock_or_custom']" />
      </div>
    </div>
  </div>
  <div
    class="flex items-center justify-center px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md sticky bottom-0">
    <PrimaryButton @click.prevent="saveForm()" :disabled="saving" v-if="hideSave === false">Save</PrimaryButton>
  </div>
</template>
